import { isKnownApiErrorCode, t, translateApiError } from '../../common/i18n.js'
import type { PortalApiErrorBody } from '../../common/portal-api-errors.js'
import type { Language } from '../../common/types/i18n.js'

const isErrorBody = (body: unknown): body is Partial<PortalApiErrorBody> =>
    typeof body === 'object' && body !== null

/**
 * Turn an API error response body into the user-facing message for `lang`.
 * Shared by the portal API client, the legacy http client and the feedback
 * widget. Pure (no fetch, no global state) so it can be unit-tested.
 *
 * Resolution order:
 *   1. a `code` the client knows → translated (with `params`)
 *   2. a `code` the client does not know yet (server newer than the bundle),
 *      or no code at all → the server's `message`, then `error`
 *   3. nothing usable (empty / non-object body) → a translated generic
 *      "request failed (status)" line — never a bare `Error N`.
 */
export const errorMessageFromBody = (body: unknown, status: number, lang: Language): string => {
    if (isErrorBody(body)) {
        if (body.code && isKnownApiErrorCode(body.code)) {
            return translateApiError(body.code, (body.params ?? {}) as never, lang)
        }
        if (body.message) return body.message
        if (body.error) return body.error
    }
    return t.common.httpError(status)(lang)
}
